// Domain-accurate mock data for KaratScan
// Mirrors the public API shapes: PublicItemSummary / PricingSummary / WeightSummary etc.
// NOTE: monetary/ratio/weight numbers would arrive as strings from the API; we keep
// numbers here and format in the UI layer.

const SAMPLE_TITLES = [
  "14K Yellow Gold Rope Chain Necklace 24in 18.4g — Italy",
  "Vintage 18K Solid Gold Cuban Link Bracelet 8in",
  "10K Gold Diamond Tennis Bracelet 7.25in — 4.2ctw",
  "22K Indian Wedding Bangle Pair — 32.6g Hallmarked",
  "Estate 14K Gold Signet Ring Sz 10 — 12.8g",
  "Solid 18K Yellow Gold Curb Chain 22in — 41.7g",
  "Antique Victorian 9K Rose Gold Locket — 8.9g",
  "Men's 14K White Gold Wedding Band 7mm — 9.2g",
  "Vintage Italian 18K Gold Mariner Chain 20in — 22.1g",
  "Solid 14K Gold Hoop Earrings 30mm — 5.4g Pair",
  "Estate 18K Gold Emerald Cocktail Ring Sz 7",
  "10K Gold Figaro Chain Necklace 26in — 14.8g",
  "18K Gold Byzantine Chain Bracelet 7.5in",
  "Heavy 21K Gold Bangle — 28.4 grams, stamped",
  "14K Gold Herringbone Necklace 18in — 11.2g",
  "24K Pure Gold Baby Bracelet — 5.1g CGT",
  "18K Gold Rolo Chain 16in — weight not stated",
  "Vintage 14K Gold Charm Bracelet w/ 9 charms",
];

// API reference taxonomies (would come from GET /reference/*)
const ITEM_TYPES = ["ring","necklace","bracelet","earrings","pendant","chain","brooch","bangle","pin","watch","charm","anklet","set","other"];
const METALS = ["gold","silver","platinum","palladium","titanium","stainless_steel","brass","copper","other","unknown"];
const KARAT_PURITY = { 8:0.3333, 9:0.375, 10:0.4167, 12:0.50, 14:0.5833, 18:0.75, 20:0.8333, 21:0.875, 22:0.9167, 24:1.0 };
const KARATS = [8,9,10,12,14,18,20,21,22,24];
const CONDITIONS = ["new","like_new","excellent","good","fair","poor"];
const CONDITION_LABEL = { new:"New", like_new:"Like new", excellent:"Excellent", good:"Good", fair:"Fair", poor:"Poor" };
const CONFIDENCE_LEVELS = ["high","medium","low"];
const SORT_OPTIONS = [
  { value:"newest", label:"Newest" },
  { value:"price_to_melt_asc", label:"Best deals" },
  { value:"deal_score_desc", label:"Value tier" },
  { value:"listing_time_desc", label:"Listing time" },
];

const SELLERS = ["midwest_estate_co","goldlinks_nyc","vintage_jewels_ltd","the_karat_room","italianjewelry_direct","pawn_premium_la","estatejeweler99","gold_loop_treasures"];
const LOCATIONS = ["Los Angeles, CA","New York, NY","Miami, FL","Chicago, IL","Brooklyn, NY","Dallas, TX","Boston, MA","Atlanta, GA","Phoenix, AZ"];

// Title builder — keeps title coherent with the item's actual attributes
const GOLD_COLORS = ["Yellow", "White", "Rose", "Two-Tone"];
const TYPE_STYLES = {
  necklace: ["Rope Chain Necklace","Herringbone Necklace","Byzantine Necklace","Figaro Necklace"],
  chain: ["Cuban Link Chain","Mariner Chain","Figaro Chain","Curb Chain","Rope Chain"],
  bracelet: ["Cuban Link Bracelet","Tennis Bracelet","Byzantine Bracelet","Charm Bracelet"],
  bangle: ["Bangle","Wedding Bangle","Hinged Bangle","Bangle Pair"],
  ring: ["Signet Ring","Band Ring","Cocktail Ring","Wedding Band"],
  earrings: ["Hoop Earrings","Stud Earrings","Drop Earrings"],
  pendant: ["Heart Pendant","Cross Pendant","Pavé Pendant","Locket Pendant"],
  brooch: ["Estate Brooch","Floral Brooch"],
  pin: ["Lapel Pin","Stick Pin"],
  watch: ["Dress Watch","Bracelet Watch"],
  charm: ["Charm","Bracelet Charm"],
  anklet: ["Anklet","Chain Anklet"],
  set: ["Jewelry Set","Necklace & Earring Set"],
  other: ["Gold Piece","Estate Lot"],
};
const PREFIXES = ["", "Vintage ", "Estate ", "Antique ", "Solid ", "Designer ", "Heavy "];
const SUFFIXES = ["", " — Italy", " Hallmarked", ", Stamped", " — Estate Find", ""];

function buildTitle(a) {
  const style = pick(TYPE_STYLES[a.itemType] || TYPE_STYLES.other);
  const prefix = pick(PREFIXES);
  const suffix = pick(SUFFIXES);
  const wt = a.totalWeight != null ? ` — ${a.totalWeight}g` : " — weight not stated";
  if (a.isNonGold) {
    const m = a.primaryMetal === "silver" ? "Sterling Silver" : a.primaryMetal.charAt(0).toUpperCase() + a.primaryMetal.slice(1);
    return `${prefix}${m} ${style}${wt}`.trim();
  }
  const highKarat = a.karat >= 21;
  const colorPool = highKarat ? ["Yellow"] : GOLD_COLORS;
  const color = (a.itemType === "ring" || a.itemType === "pendant") && chance(0.4) ? "" : pick(colorPool) + " ";
  let metalWord = "Gold";
  if (a.isPlated) metalWord = "Gold Plated";
  else if (a.isFilled) metalWord = "Gold Filled";
  else if (a.isVermeil) metalWord = "Vermeil";
  return `${prefix}${a.karat}K ${color}${metalWord} ${style}${wt}${suffix}`.replace(/\s+/g, " ").trim();
}

// Tier metadata mirrors GET /reference/deal-tiers. Keep these fallbacks in sync
// with the backend defaults so mock mode and reference-fetch failures remain
// usable without inventing a second live source of truth.
const DEFAULT_DEAL_TIER_ENTRIES = [
  {
    code: "todays_deals",
    label: "Today's Deals",
    tooltip: "Listed in the last 24 hours and priced at or below 1.40x estimated melt value.",
    display_order: 5,
    max_price_to_melt_ratio: "1.40",
    near_miss_multiplier: null,
    value_tier_code: null,
    requires_best_offer: false,
    is_value_band: false,
    is_near_miss_eligible: false,
  },
  {
    code: "deep_discount",
    label: "Under Melt",
    tooltip: "Asking price is at or below estimated melt value; the strongest value band.",
    display_order: 10,
    max_price_to_melt_ratio: "1.00",
    near_miss_multiplier: null,
    value_tier_code: "S",
    requires_best_offer: false,
    is_value_band: true,
    is_near_miss_eligible: true,
  },
  {
    code: "steal",
    label: "Great Deal",
    tooltip: "Asking price is at or below 1.20x estimated melt value; includes Under Melt items.",
    display_order: 20,
    max_price_to_melt_ratio: "1.20",
    near_miss_multiplier: null,
    value_tier_code: "A",
    requires_best_offer: false,
    is_value_band: true,
    is_near_miss_eligible: true,
  },
  {
    code: "deal",
    label: "Good Deal",
    tooltip: "Asking price is at or below 1.40x estimated melt value; the broadest value band.",
    display_order: 30,
    max_price_to_melt_ratio: "1.40",
    near_miss_multiplier: null,
    value_tier_code: "B",
    requires_best_offer: false,
    is_value_band: true,
    is_near_miss_eligible: true,
  },
  {
    code: "negotiable",
    label: "Accepts Offers",
    tooltip: "Seller has Best Offer enabled and the asking price is inside the Good Deal band.",
    display_order: 40,
    max_price_to_melt_ratio: "1.40",
    near_miss_multiplier: null,
    value_tier_code: null,
    requires_best_offer: true,
    is_value_band: false,
    is_near_miss_eligible: false,
  },
  {
    code: "near",
    label: "Price Drop Watch",
    tooltip: "Items just outside the selected value band. A small price drop would move them into that band.",
    display_order: 50,
    max_price_to_melt_ratio: null,
    near_miss_multiplier: "1.05",
    value_tier_code: null,
    requires_best_offer: false,
    is_value_band: false,
    is_near_miss_eligible: false,
  },
  {
    code: "unscored_gold",
    label: "Unscored Gold",
    tooltip: "Confirmed solid-gold listings that do not currently have a deal ratio.",
    display_order: 60,
    max_price_to_melt_ratio: null,
    near_miss_multiplier: null,
    value_tier_code: null,
    requires_best_offer: false,
    is_value_band: false,
    is_near_miss_eligible: false,
  },
];

const TIER = {
  deep_discount: { max: 1.00, label: "Under Melt", blurb: "At or under melt" },
  steal: { max: 1.20, label: "Great Deal", blurb: "Great deal" },
  deal:  { max: 1.40, label: "Good Deal",  blurb: "Good deal" },
};
const TIER_LABELS = {
  todays_deals: "Today's Deals",
  deep_discount: "Under Melt",
  offer: "Accepts Offers",
  steal: "Great Deal",
  deal: "Good Deal",
  negotiable: "Accepts Offers",
  near: "Price Drop Watch",
  unscored_gold: "Unscored Gold",
  needs: "Unscored Gold",
  all: "All items",
};
const DEAL_TIER_META = {};
let DEAL_TIER_ENTRIES = [];
const DEAL_TIER_VALUE_TIER_FALLBACKS = {
  deep_discount: "S",
  steal: "A",
  deal: "B",
};
const SPOT_PER_GRAM = 77.42; // USD per gram pure gold (≈ $2,408/oz)

// Reason codes reference (would come from GET /reference/reason-codes)
const REASON_CODES = {
  explicit_gold_weight: { label: "Explicit gold weight", category: "priced",
    tooltip: "Listing stated the gold weight explicitly; melt value is computed directly from the stated weight." },
  clean_total_weight: { label: "Clean total weight", category: "priced",
    tooltip: "No stones present; the listing's total weight is treated as the metal weight." },
  hallmark_confirmed: { label: "Hallmark confirmed", category: "priced",
    tooltip: "A karat hallmark was detected in the listing photos or text." },
  image_scale_estimate: { label: "Weight estimated from photos", category: "review_required",
    tooltip: "Gold weight was inferred from image-scale analysis rather than a stated weight; treat as an estimate." },
  packaging_weight_risk: { label: "Packaging weight risk", category: "risk",
    tooltip: "Listed weight may include packaging; not safe to price as item weight." },
  hollow_requires_review: { label: "Hollow item requires review", category: "review_required",
    tooltip: "Item is hollow; actual gold content is below the nominal weight and the result needs human review." },
  missing_weight: { label: "Missing weight", category: "not_priced",
    tooltip: "Item weight could not be determined from the listing." },
  missing_purity: { label: "Missing purity", category: "not_priced",
    tooltip: "Gold purity (karat) could not be determined." },
  gold_plated_not_priced: { label: "Gold plated — not priced", category: "not_priced",
    tooltip: "Gold-plated items have negligible melt value and are not priced." },
  gold_filled_not_priced: { label: "Gold filled — not priced", category: "not_priced",
    tooltip: "Gold-filled items contain too little gold to price reliably." },
  vermeil_not_priced: { label: "Vermeil — not priced", category: "not_priced",
    tooltip: "Vermeil (gold-plated sterling silver) is not priced as gold." },
  non_gold_not_priced: { label: "Non-gold item — not priced", category: "non_jewelry",
    tooltip: "Primary metal is not gold; KaratScan only prices gold items." },
  stones_present: { label: "Stones deducted", category: "audit",
    tooltip: "Gemstones were detected; their estimated weight was deducted before valuing the gold." },
};

const REASON_CAT_TONE = {
  priced: "live", review_required: "gold", not_priced: "neutral",
  non_jewelry: "neutral", risk: "hot", audit: "info", other: "neutral",
};

function rand(min, max) { return min + Math.random() * (max - min); }
function randi(min, max) { return Math.floor(rand(min, max + 1)); }
function pick(arr) { return arr[randi(0, arr.length - 1)]; }
function chance(p) { return Math.random() < p; }

function canonicalDealTierCode(code) {
  if (code === "offer") return "negotiable";
  if (code === "needs") return "unscored_gold";
  return code;
}

function canonicalNearMissTierCode(code) {
  return code === "offer" ? "deep_discount" : code;
}

function normalizeDealTierEntry(entry) {
  if (!entry || !entry.code) return null;
  const code = canonicalDealTierCode(String(entry.code));
  const max = entry.max_price_to_melt_ratio ?? entry.maxPriceToMeltRatio ?? null;
  const nearMult = entry.near_miss_multiplier ?? entry.nearMissMultiplier ?? null;
  const rawValueTier = entry.value_tier_code ?? entry.valueTierCode ?? DEAL_TIER_VALUE_TIER_FALLBACKS[code] ?? null;
  return {
    code,
    label: entry.label || TIER_LABELS[code] || code,
    tooltip: entry.tooltip || null,
    displayOrder: Number(entry.display_order ?? entry.displayOrder ?? 999),
    maxPriceToMeltRatio: max == null ? null : Number(max),
    nearMissMultiplier: nearMult == null ? null : Number(nearMult),
    valueTierCode: rawValueTier ? String(rawValueTier).toUpperCase() : null,
    requiresBestOffer: Boolean(entry.requires_best_offer ?? entry.requiresBestOffer ?? false),
    isValueBand: Boolean(entry.is_value_band ?? entry.isValueBand ?? false),
    isNearMissEligible: Boolean(entry.is_near_miss_eligible ?? entry.isNearMissEligible ?? false),
  };
}

function setDealTierEntries(entries) {
  const source = Array.isArray(entries) && entries.length
    ? entries
    : DEFAULT_DEAL_TIER_ENTRIES;
  const normalized = source
    .map(normalizeDealTierEntry)
    .filter(Boolean)
    .sort((a, b) => a.displayOrder - b.displayOrder);

  Object.keys(DEAL_TIER_META).forEach((key) => { delete DEAL_TIER_META[key]; });
  normalized.forEach((entry) => {
    DEAL_TIER_META[entry.code] = entry;
    TIER_LABELS[entry.code] = entry.label;
    if (entry.code === "negotiable") {
      DEAL_TIER_META.offer = entry;
      TIER_LABELS.offer = entry.label;
    }
    if (TIER[entry.code] && Number.isFinite(entry.maxPriceToMeltRatio)) {
      TIER[entry.code].max = entry.maxPriceToMeltRatio;
      TIER[entry.code].label = entry.label;
      TIER[entry.code].blurb = entry.tooltip || TIER[entry.code].blurb;
    }
  });
  DEAL_TIER_ENTRIES = normalized;
  return normalized;
}

function dealTierLabel(code, fallback) {
  const canonical = canonicalDealTierCode(code);
  return TIER_LABELS[canonical] || TIER_LABELS[code] || fallback || code;
}

function dealTierTooltip(code) {
  const canonical = canonicalDealTierCode(code);
  return (DEAL_TIER_META[canonical] && DEAL_TIER_META[canonical].tooltip) || "";
}

function dealTierThreshold(code) {
  const canonical = canonicalDealTierCode(code);
  const entry = DEAL_TIER_META[canonical];
  if (entry && Number.isFinite(entry.maxPriceToMeltRatio)) {
    return entry.maxPriceToMeltRatio;
  }
  return TIER[canonical] && Number.isFinite(TIER[canonical].max) ? TIER[canonical].max : null;
}

function dealTierRequiresBestOffer(code) {
  const canonical = canonicalDealTierCode(code);
  const entry = DEAL_TIER_META[canonical];
  return Boolean(entry && entry.requiresBestOffer);
}

function dealTierValueTier(code) {
  const canonical = canonicalDealTierCode(code);
  const entry = DEAL_TIER_META[canonical];
  return (entry && entry.valueTierCode) || DEAL_TIER_VALUE_TIER_FALLBACKS[canonical] || null;
}

function dealTierPresentation(code, fallback) {
  const canonical = canonicalDealTierCode(code);
  const label = dealTierLabel(canonical, fallback);
  const valueTier = dealTierValueTier(canonical);
  const thresholdText = dealTierThresholdText(canonical);
  const tooltip = dealTierTooltip(canonical);
  const ariaParts = [label];
  if (valueTier) ariaParts.push(`${valueTier} tier`);
  if (thresholdText) ariaParts.push(thresholdText);
  return {
    code: canonical,
    label,
    valueTier,
    thresholdText,
    tooltip,
    ariaLabel: ariaParts.join(", "),
  };
}

function nearMissMultiplier() {
  const entry = DEAL_TIER_META.near;
  return entry && Number.isFinite(entry.nearMissMultiplier)
    ? entry.nearMissMultiplier
    : 1.05;
}

function dealTierThresholdText(code, fallback) {
  const threshold = dealTierThreshold(code);
  if (threshold == null) return fallback || "";
  const base = "≤ " + threshold.toFixed(2) + "x melt";
  return dealTierRequiresBestOffer(code) ? "Best Offer + " + base : base;
}

setDealTierEntries(DEFAULT_DEAL_TIER_ENTRIES);

let _id = 100000;
function nextId() { _id += randi(7, 113); return String(_id); }
function ebayId() { return "3" + randi(10000000, 99999999) + "" + randi(100, 999); }

// derive the tightest tier an item's ratio qualifies for
function tierFor(ratio) {
  if (ratio == null) return null;
  if (ratio <= dealTierThreshold("deep_discount")) return "deep_discount";
  if (ratio <= dealTierThreshold("steal")) return "steal";
  if (ratio <= dealTierThreshold("deal")) return "deal";
  return null;
}
// near-miss = just outside the selected value band.
function isNearMiss(ratio, baseTier = "steal") {
  if (ratio == null) return false;
  const threshold = dealTierThreshold(canonicalNearMissTierCode(baseTier));
  if (threshold == null) return false;
  return ratio > threshold && ratio <= threshold * nearMissMultiplier();
}

function valueScoreForRatio(ratio) {
  if (ratio == null) return null;
  // Internal 0-100 score: 100 is at/below melt, 0 is at/above 1.80x melt.
  const s = Math.round((1.8 - ratio) / (1.8 - 1.0) * 100);
  return Math.max(0, Math.min(100, s));
}

const dealScoreFor = valueScoreForRatio;

function valueTierForRatio(ratio) {
  if (ratio == null) return null;
  if (ratio <= 1.00) return "S";
  if (ratio <= 1.20) return "A";
  if (ratio <= 1.40) return "B";
  if (ratio <= 1.60) return "C";
  if (ratio <= 1.80) return "D";
  return "F";
}

function makeItem(opts = {}) {
  const id = nextId();
  const type = opts.item_type ?? pick(["necklace","bracelet","ring","chain","bangle","earrings","pendant"]);
  const seller = pick(SELLERS);
  const location = pick(LOCATIONS);

  // material posture
  const metalRoll = Math.random();
  const isSolidGold = metalRoll < 0.80;
  const isPlated = metalRoll >= 0.80 && metalRoll < 0.88;
  const isFilled = metalRoll >= 0.88 && metalRoll < 0.93;
  const isVermeil = metalRoll >= 0.93 && metalRoll < 0.96;
  const isNonGold = metalRoll >= 0.96;
  const primaryMetal = isNonGold ? pick(["silver","platinum","brass"]) : "gold";

  const karat = isNonGold ? null : (opts.karat ?? pick([9,10,14,18,21,22,24]));
  const purity = karat ? KARAT_PURITY[karat] : 0;

  // weight basis
  const weightUnknown = opts.needsWeight ?? (isSolidGold && chance(0.12));
  const weightBasis = weightUnknown ? "none" : (chance(0.72) ? "text" : "image");
  const totalWeight = weightUnknown ? null : +(rand(2.4, 48)).toFixed(1);
  const hasStones = chance(0.28);
  const goldWeight = weightUnknown ? null
    : +(totalWeight * (hasStones ? rand(0.78, 0.94) : 1)).toFixed(1);

  // pricing
  let pricingStatus, requiresReview = false, ratio = null, meltValue = null, valueScore = null, valueTier = null, dealScore = null;
  const reasonCodes = [];
  let weightConfidence = "high", weightRequiresReview = false;
  let overallConfidence = "high";

  if (isNonGold) {
    pricingStatus = "skipped"; reasonCodes.push("non_gold_not_priced");
    overallConfidence = "medium";
  } else if (isPlated) {
    pricingStatus = "skipped_policy"; reasonCodes.push("gold_plated_not_priced");
    overallConfidence = "medium";
  } else if (isFilled) {
    pricingStatus = "skipped_policy"; reasonCodes.push("gold_filled_not_priced");
    overallConfidence = "medium";
  } else if (isVermeil) {
    pricingStatus = "skipped_policy"; reasonCodes.push("vermeil_not_priced");
    overallConfidence = "medium";
  } else if (weightUnknown) {
    pricingStatus = "skipped"; reasonCodes.push("missing_weight");
    overallConfidence = "low";
  } else {
    // priced
    meltValue = +(goldWeight * purity * SPOT_PER_GRAM).toFixed(2);
    ratio = opts.ratio ?? +(rand(0.52, 1.16)).toFixed(2);
    valueScore = valueScoreForRatio(ratio);
    valueTier = valueTierForRatio(ratio);
    dealScore = valueScore;

    if (weightBasis === "image") {
      requiresReview = true; weightRequiresReview = true; weightConfidence = chance(0.5) ? "medium" : "low";
      reasonCodes.push("image_scale_estimate");
      overallConfidence = chance(0.5) ? "medium" : "low";
    } else {
      reasonCodes.push(hasStones ? "stones_present" : (chance(0.5) ? "explicit_gold_weight" : "clean_total_weight"));
      weightConfidence = chance(0.7) ? "high" : "medium";
      overallConfidence = chance(0.7) ? "high" : "medium";
    }
    // risk flags
    if (chance(0.10)) { requiresReview = true; weightRequiresReview = true; reasonCodes.push("packaging_weight_risk"); }
    if (type === "bangle" && chance(0.25)) { requiresReview = true; reasonCodes.push("hollow_requires_review"); }
    if (chance(0.2)) reasonCodes.push("hallmark_confirmed");

    pricingStatus = requiresReview ? "priced_review_required" : "priced";
  }

  const price = meltValue != null ? +(meltValue * ratio).toFixed(2) : +(rand(60, 1400)).toFixed(2);
  const shipping = pick([0, 0, 4.99, 6.95, 9.99, 12.5]);
  const allIn = +(price + shipping).toFixed(2);

  const coverage = weightUnknown ? pick(["indexed","enriched"]) : (chance(0.85) ? "parsed" : "enriched");
  const ageMin = opts.ageMin ?? randi(1, 900);

  const title = opts.title ?? buildTitle({ itemType: type, karat, totalWeight, primaryMetal, isNonGold, isPlated, isFilled, isVermeil });

  return {
    id, itemId: ebayId(), title, listingUrl: "#",
    itemType: type, primaryMetal, karat, purity,
    totalWeight, goldWeight, weightBasis, weightConfidence, weightRequiresReview,
    hasStones, isSolidGold, isPlated, isFilled, isVermeil, isNonGold,
    meltValue, ratio, valueScore, valueTier, dealScore, pricingStatus, requiresReview, reasonCodes,
    spotPerGram: SPOT_PER_GRAM,
    price, shipping, allIn, currency: "USD",
    overallConfidence, coverage,
    tier: tierFor(ratio), nearMiss: isNearMiss(ratio),
    seller, location,
    sellerFeedbackScore: randi(48, 12480),
    sellerFeedbackPercent: +(rand(96.2, 100)).toFixed(1),
    authenticityGuarantee: chance(0.22),
    bestOffer: chance(0.4), buyItNow: true,
    images: randi(1, 12),
    isSavedByMe: null, // null when anonymous
    ageMin,
    condition: pick(CONDITIONS),
    note: null,
  };
}

// Build a seed set with good tier distribution
function buildSeed(n) {
  const arr = [];
  // guarantee a spread of tiers
  for (let i = 0; i < n; i++) {
    let it;
    const r = i / n;
    if (r < 0.18) it = makeItem({ ratio: +rand(0.55, 0.99).toFixed(2) });       // under melt
    else if (r < 0.40) it = makeItem({ ratio: +rand(1.00, 1.20).toFixed(2) });  // great deals
    else if (r < 0.66) it = makeItem({ ratio: +rand(1.21, 1.40).toFixed(2) });  // good deals
    else if (r < 0.78) it = makeItem({ ratio: +rand(1.41, 1.47).toFixed(2) });  // near-miss
    else if (r < 0.88) it = makeItem({ needsWeight: true });                    // needs weight
    else it = makeItem();                                                        // random
    arr.push(it);
  }
  return arr;
}

const SEED_ITEMS = buildSeed(30);
SEED_ITEMS.sort((a,b) => a.ageMin - b.ageMin);

// Gold spot (GET /gold/spot)
const GOLD_SPOT = {
  pricePerGram: SPOT_PER_GRAM,
  pricePerOz: +(SPOT_PER_GRAM * 31.1035).toFixed(2),
  source: "live",            // live | stale_cache | fallback
  isAuthoritative: true,
  ageSeconds: 42,
  change24h: +0.42,
};

const SAVED_SEARCHES = [
  { id:"s1", name: "Solid 14K+ under melt", count: 12, color: "var(--accent)", crit: "14K,18K,22K · solid · ≤1.00×" },
  { id:"s2", name: "Heavy chains > 20g", count: 7, color: "var(--accent-deep)", crit: "chain · ≥20g · 14K+" },
  { id:"s3", name: "Cuban link 18K offers", count: 2, color: "var(--hot)", crit: "“cuban” · 18K · Best Offer · ≤1.40×" },
  { id:"s4", name: "22K hallmarked great deals", count: 5, color: "var(--info)", crit: "22K · hallmark · ≤1.20×" },
];

// Alert rules (GET /me/alert-rules)
const ALERTS = [
  { id:"a1", name:"Underpriced 18K (≥10g)", enabled:true, version:4,
    criteria:"18K · solid · ≥10g · ≤1.00× melt", channels:["discord"],
    lastTriggered:"4m ago", matches24h:11, tier:"deep_discount" },
  { id:"a2", name:"Heavy 22K bangles", enabled:true, version:2,
    criteria:"22K · bangle · ≥20g · ≤1.40×", channels:["discord","email"],
    lastTriggered:"38m ago", matches24h:6, tier:"deal" },
  { id:"a3", name:"Any chain below melt", enabled:false, version:7,
    criteria:"chain · 14K+ · ≤1.00× · conf ≥ medium", channels:["discord"],
    lastTriggered:"yesterday", matches24h:0, tier:"deep_discount" },
  { id:"a4", name:"24K pure gold drops", enabled:true, version:1,
    criteria:"24K · any type · Best Offer · ≤1.40×", channels:["web_push"],
    lastTriggered:"2h ago", matches24h:3, tier:"offer" },
];

// Alert history (GET /me/alerts)
const ALERT_HISTORY = [
  { id:"h1", rule:"Underpriced 18K (≥10g)", ruleVersion:4, status:"sent", sentAt:"4m ago", item:"18K Cuban Link Bracelet · 14.2g", ratio:0.74, channel:"discord", attempts:1 },
  { id:"h2", rule:"Heavy 22K bangles", ruleVersion:2, status:"sent", sentAt:"38m ago", item:"22K Wedding Bangle Pair · 32.6g", ratio:0.83, channel:"discord", attempts:1 },
  { id:"h3", rule:"Underpriced 18K (≥10g)", ruleVersion:4, status:"pending", sentAt:"—", item:"18K Curb Chain · 41.7g", ratio:0.71, channel:"email", attempts:2, nextRetry:"in 3m" },
  { id:"h4", rule:"24K pure gold drops", ruleVersion:1, status:"failed", sentAt:"2h ago", item:"24K Baby Bracelet · 5.1g", ratio:0.80, channel:"web_push", attempts:3, error:"endpoint expired" },
  { id:"h5", rule:"Heavy 22K bangles", ruleVersion:2, status:"sent", sentAt:"3h ago", item:"21K Gold Bangle · 28.4g", ratio:0.88, channel:"discord", attempts:1 },
];

// Notification channels (GET /me/notification-channels)
const CHANNELS = [
  { id:"c1", type:"discord_webhook", label:"#deal-hunting", fingerprint:"a91f…7c2", enabled:true, status:"verified" },
  { id:"c2", type:"discord_webhook", label:"Personal DMs", fingerprint:"4b30…e9a", enabled:true, status:"verified" },
  { id:"c3", type:"email", label:"me@example.com", fingerprint:null, enabled:false, status:"coming_soon" },
  { id:"c4", type:"web_push", label:"This browser", fingerprint:null, enabled:false, status:"coming_soon" },
];

const ACTIVITY = [
  { t:"now", text:"Live stream connected", kind:"ok" },
  { t:"12s", text:"New offer · 18K cuban bracelet · 1.18× melt", kind:"match" },
  { t:"1m", text:"Alert fired · Underpriced 18K (≥10g)", kind:"alert" },
  { t:"3m", text:"412 new listings analyzed", kind:"info" },
  { t:"6m", text:"2 deals matched · Heavy 22K bangles", kind:"match" },
  { t:"9m", text:"Gold spot refreshed · $77.42/g", kind:"info" },
  { t:"14m", text:"New great deal · 14K herringbone · 1.18× melt", kind:"match" },
];

export {
  ACTIVITY,
  ALERTS,
  ALERT_HISTORY,
  CHANNELS,
  CONDITION_LABEL,
  CONDITIONS,
  CONFIDENCE_LEVELS,
  DEAL_TIER_ENTRIES,
  DEAL_TIER_META,
  DEFAULT_DEAL_TIER_ENTRIES,
  GOLD_SPOT,
  ITEM_TYPES,
  KARAT_PURITY,
  KARATS,
  METALS,
  REASON_CAT_TONE,
  REASON_CODES,
  SAVED_SEARCHES,
  SEED_ITEMS,
  SORT_OPTIONS,
  SPOT_PER_GRAM,
  TIER,
  TIER_LABELS,
  buildSeed,
  canonicalDealTierCode,
  canonicalNearMissTierCode,
  chance,
  dealScoreFor,
  dealTierLabel,
  dealTierPresentation,
  dealTierRequiresBestOffer,
  dealTierThreshold,
  dealTierThresholdText,
  dealTierTooltip,
  dealTierValueTier,
  isNearMiss,
  makeItem,
  nearMissMultiplier,
  pick,
  rand,
  randi,
  setDealTierEntries,
  tierFor,
  valueScoreForRatio,
  valueTierForRatio,
};
